import { useSelector } from "react-redux";
import { actionKey, RISK_CATEGORIES, SEVERITY_RANK } from "./DataControlConstans";
import "./FileSystemControls.scss";

export function Badge({ children, variant }) {
  return <span className={`fsc-badge fsc-badge--${variant}`}>{children}</span>;
}

export function ConfirmModal({ isOpen, message, onConfirm, onCancel }) {
  if (!isOpen) return null;
  return (
    <div className="fsc-modal-overlay" onClick={onCancel}>
      <div className="fsc-modal" onClick={(e) => e.stopPropagation()}>
        <div className="fsc-modal__header">
          <div className="fsc-modal__icon">
            <i className="ti ti-shield-question" />
          </div>
          <h3 className="fsc-modal__title">Request for confirmation</h3>
        </div>
        <p className="fsc-modal__message">{message}</p>
        <div className="fsc-modal__actions">
          <button className="fsc-btn fsc-btn--ghost" onClick={onCancel}>
            No, cancel
          </button>
          <button className="fsc-btn fsc-btn--primary" onClick={onConfirm}>
            Yes, confirm
          </button>
        </div>
      </div>
    </div>
  );
}

// ── icon-or-initials — falls back to a circular initials avatar with a
// full-name tooltip when no icon asset exists for the given label ──────────

function getInitials(label) {
  if (!label) return "?";
  return label
    .replace(/_/g, " ")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();
}

export function formatTypeLabel(value) {
  return String(value)
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}


export function parseTypeList(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  return String(value)
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

export function IconOrInitials({ src, label, size = 20, className = "" }) {
  if (src) {
    return (
      <img
        src={src}
        alt={label}
        title={label}
        className={`fsc-table__source-icon ${className}`}
      />
    );
  }

  return (
    <span
      className="fsc-icon-fallback"
      title={label}
      style={{ width: size, height: size, fontSize: size * 0.42 }}
    >
      {getInitials(label)}
    </span>
  );
}

export function computeOverview(appliedActions) {
  const allItems = RISK_CATEGORIES.flatMap((category) =>
    category.items.map((item) => ({ ...item, categoryId: category.id })),
  );
  const pending = allItems.filter(
    (item) => !appliedActions[actionKey(item.categoryId, item.subject)],
  );

  const weight = { Critical: 22, High: 14, Medium: 8, Low: 4 };
  const rawScore = pending.reduce(
    (sum, item) => sum + (weight[item.severity] || 0),
    0,
  );
  const score = Math.min(100, rawScore);

  const tone = score >= 70 ? "red" : score >= 40 ? "amber" : "green";
  const riskLabel =
    score >= 70
      ? "High Risk"
      : score >= 40
        ? "Medium Risk"
        : score > 0
          ? "Low Risk"
          : "Secure";

  const bySeverity = { Critical: 0, High: 0, Medium: 0, Low: 0 };
  pending.forEach((item) => {
    bySeverity[item.severity] = (bySeverity[item.severity] || 0) + 1;
  });

  return {
    score,
    tone,
    riskLabel,
    bySeverity,
    totalPending: pending.length,
    totalItems: allItems.length,
  };
}

export function getCategoryMeta(category, appliedActions) {
  const items = category.items;
  const pendingItems = items.filter(
    (item) => !appliedActions[actionKey(category.id, item.subject)],
  );

  const metricTotal = items.reduce((sum, item) => sum + (item.metric || 0), 0);

  const riskLevel = items.reduce(
    (top, item) =>
      SEVERITY_RANK[item.severity] > SEVERITY_RANK[top] ? item.severity : top,
    items[0].severity,
  );

  const lastDetected = items.reduce(
    (latest, item) => (item.timestamp > latest ? item.timestamp : latest),
    items[0].timestamp,
  );

  const uniqueSources = [...new Set(items.map((item) => item.source))];
  const dataSource =
    uniqueSources.length === 1 ? uniqueSources[0] : "Multiple sources";

  return { pendingItems, metricTotal, riskLevel, lastDetected, dataSource };
}

export function getCategoryStats(category, appliedActions) {
  const items = category.items;
  const appliedCount = items.filter(
    (item) => appliedActions[actionKey(category.id, item.subject)],
  ).length;
  const pendingCount = items.length - appliedCount;
  const highSeverityCount = items.filter(
    (item) => item.severity === "Critical" || item.severity === "High",
  ).length;
  const metricTotal = items.reduce((sum, item) => sum + (item.metric || 0), 0);

  return [
    {
      value: items.length,
      label: "Total flagged",
      sub: "all time",
      tone: "blue",
    },
    {
      value: appliedCount,
      label: "Applied",
      sub: "active restrictions",
      tone: "green",
    },
    {
      value: pendingCount,
      label: "Pending review",
      sub: "awaiting action",
      tone: "amber",
    },
    { value: metricTotal, label: category.metricLabel, sub: "", tone: "blue" },
    {
      value: highSeverityCount,
      label: "High severity",
      sub: "need priority review",
      tone: "red",
    },
  ];
}